import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { fetchDashboardTeaser } from "../../api/queries";
import { useLanguage } from "../../contexts/LanguageContext";
import { useStore } from "../../contexts/StoreContext";
import usePlanTier, { TAB_REQUIRED_TIER, tierAllows } from "../../hooks/usePlanTier";
import Advanced from "./Advanced";
import Insights from "./Insights";
import Overview from "./Overview";
import DashboardTabs from "./components/DashboardTabs";
import RangePicker from "./components/RangePicker";
import { DashboardErrorFallback } from "./components/ui";
import { formatLongToday } from "./format";
import useDashboardParams, { DASHBOARD_TABS } from "./useDashboardParams";

function greetingKey(now = new Date()) {
  const hour = now.getHours();
  if (hour < 12) return "dashboard.header.morning";
  if (hour < 18) return "dashboard.header.afternoon";
  return "dashboard.header.evening";
}

export default function Dashboard() {
  const { selectedStore, canManageStore } = useStore();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const tier = usePlanTier(selectedStore);
  const { tab, range, setTab, setRange } = useDashboardParams();
  const storeId = selectedStore?.id;

  // One real number per locked tab. Runs for every tier (the plan says so),
  // and is cheap and cached on the API side.
  const { data: teaser } = useQuery({
    queryKey: ["dashboard", "teaser", storeId],
    queryFn: () => fetchDashboardTeaser(storeId),
    enabled: !!storeId,
  });

  const lockedTabs = DASHBOARD_TABS.filter(
    (name) => !tierAllows(tier, TAB_REQUIRED_TIER[name]),
  );
  const locked = lockedTabs.includes(tab);

  // Opens the Marketing push form with the segment already chosen; Marketing
  // reads the params once and drops them from the URL.
  const createSegmentPush = useCallback(
    (segment) => {
      const params = new URLSearchParams({ compose: "push", segment });
      navigate(`/marketing?${params.toString()}`);
    },
    [navigate],
  );

  const title =
    tab === "overview" ? t(greetingKey()) : t(`dashboard.header.${tab}`);

  return (
    <div className="flex max-w-[1240px] flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="text-[13px] font-medium text-gray-600">
            {formatLongToday(language)}
          </span>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-[28px]">
            {title}
          </h1>
        </div>
        {tab !== "advanced" && <RangePicker value={range} onChange={setRange} />}
      </header>

      <DashboardTabs
        tabs={DASHBOARD_TABS}
        active={tab}
        lockedTabs={lockedTabs}
        onSelect={setTab}
      />

      <div
        id="dashboard-tabpanel"
        role="tabpanel"
        aria-labelledby={`dashboard-tab-${tab}`}
      >
        <ErrorBoundary
          FallbackComponent={DashboardErrorFallback}
          resetKeys={[tab, storeId, range]}
        >
          {/* key={storeId}: switching store from the header remounts the tab,
              so segment, page and export state start over for the new store. */}
          {tab === "overview" && (
            <Overview key={storeId} storeId={storeId} range={range} tier={tier} teaser={teaser} />
          )}
          {tab === "insights" && (
            <Insights
              key={storeId}
              storeId={storeId}
              range={range}
              locked={locked}
              teaser={teaser}
              timeZone={selectedStore?.time_zone}
              canPush={canManageStore}
              canExport={canManageStore && tierAllows(tier, "advanced")}
              onCreatePush={createSegmentPush}
            />
          )}
          {tab === "advanced" && (
            <Advanced
              key={storeId}
              storeId={storeId}
              locked={locked}
              teaser={teaser}
              weeklyReportEnabled={selectedStore?.weekly_report_enabled}
              canManageStore={canManageStore}
            />
          )}
        </ErrorBoundary>
      </div>
    </div>
  );
}
